import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { AuditTrail } from '@/components/audit/AuditTrail';
import {
  createFileRoute,
  getRouteApi,
  Link,
  useRouter,
  Outlet,
  useChildMatches,
} from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  Check,
  ArrowUpRight,
  Briefcase,
  ChevronLeft,
  Clock3,
  FileText,
  Lock,
  MoveRight,
  Paperclip,
  Tags as TagsIcon,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import { useEstimate } from '@/hooks/estimates/useEstimate';
import { useEstimateAttachments } from '@/hooks/estimates/useEstimateAttachments';
import { useHasPermission, useIsFieldHidden } from '@/hooks/usePermissions';
import { EstimateAttachments } from '@/components/organisms/estimates/EstimateAttachments';
import { FinancialSummaryBlock } from '@/components/organisms';
import { DetailCard, DetailField, DetailFieldGrid } from '@/components/molecules';
import type { Attachment } from '@/types/attachment';
import { useContactActiveMembership } from '@/hooks/contacts/useContactMemberships';
import { useCustomFieldValues } from '@/hooks/custom-fields/useCustomFieldValues';
import { useCustomFieldSchema } from '@/hooks/custom-fields/useCustomFieldSchema';
import { formatCustomFieldDisplayValue } from '@/components/organisms/custom-fields/custom-field-form-utils';
import { CustomFieldsReadEditCard } from '@/components/organisms/custom-fields/CustomFieldsReadEditCard';
import type { CustomFieldEntityType } from '@/types/custom-fields';
import { TagSelector } from '@/components/organisms/TagSelector';
import { DEFAULT_JOB_LIST_FILTERS } from '@/components/organisms/jobs/JobListPanel';
import { useReplaceEstimateTags } from '@/hooks/estimates/useReplaceEstimateTags';
import { useRepriceEstimate } from '@/hooks/estimates/useRepriceEstimate';
import { useSaveEstimateCustomFieldValues } from '@/hooks/estimates/useSaveEstimateCustomFieldValues';
import { EstimateActions } from '@/components/organisms/estimates/EstimateActions';
import {
  DynamicBasisText,
  formatCurrency,
  formatDate,
} from '@/components/organisms/estimates/support/estimate-ui-helpers';
import { formatDateOnly } from '@/lib/dates/date-only';
import { StatusBadge } from '@/components/molecules/StatusBadge';
import { IrisAttributionBadge } from '@/components/molecules/IrisAttributionBadge';
import { DetailPageLayout } from '@/components/layouts';
import type {
  Estimate,
  EstimateStatus,
  EstimatePreviousVersion,
  EstimateLineItem,
} from '@/types/estimate';
import { pricesFromHours } from '@/lib/sales/pricingMode';
import { SalesLineItemsReadOnlyTable } from '@/components/organisms/sales/SalesLineItemsReadOnlyTable';
import { lineItemBadges } from '@/lib/estimate/lineItemBadges';
import { estimateForAcceptedScope, hasAcceptedEstimateScope } from '@/lib/estimate/acceptedScope';
import { SalesAddressBlock } from '@/components/organisms/sales/SalesAddressBlock';
import { SalesContactBlock } from '@/components/organisms/sales/SalesContactBlock';
import {
  SalesLifecycleStepper,
  type SalesLifecycleDetour,
  type SalesLifecycleStep,
} from '@/components/organisms/sales/SalesLifecycleStepper';
import { PaymentScheduleSection } from '@/components/organisms/estimates/PaymentScheduleSection';
import { WisetackContextCard } from '@/components/organisms/financing/WisetackContextCard';
import { getEstimateNextStepCopy } from '@/components/organisms/estimates/support/estimate-next-step';
import { estimateHeaderSubtitle } from '@/components/organisms/estimates/support/estimate-header-subtitle';
import { useBusinessUnitLabel } from '@/components/organisms/business-units/useBusinessUnitLabel';
import { useWisetackSettings } from '@/hooks/settings/useWisetackSettings';
import { formatEstimateSource } from '@/lib/constants/estimate-sales-attribution';
import { formatRecurringDate } from '@/lib/sales/recurring-format';
import { reconciledTaxBreakdown } from '@/lib/tax/tax-utils';
import DOMPurify from 'dompurify';
import { formatLabel, isHtmlContent } from '@/lib/utils';
import { resolveCustomerTerms } from '@/lib/terms/clientTerms';
import { RICH_TEXT_CLASS } from '@/lib/rich-text';
import type { PublicWisetackCta } from '@/types/public';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';
import { soldLineSet } from '@/lib/sales/service-components';
import { paymentMethodLabel } from '@/components/organisms/payments/payments-shared';
import { formatEstimateAddress } from './_shared';

const ESTIMATE_TABS = ['overview', 'financials', 'notes', 'internal'] as const;
type EstimateTab = (typeof ESTIMATE_TABS)[number];

export const Route = createFileRoute('/_dashboard/dashboard/estimates/$id')({
  validateSearch: (search): { tab?: EstimateTab } => ({
    tab: ESTIMATE_TABS.includes(search.tab as EstimateTab)
      ? (search.tab as EstimateTab)
      : undefined,
  }),
  component: EstimateDetailPage,
});

// Parent index route — read its search so the back breadcrumb returns to the
// same filtered/sorted/paged estimates list (NOT-228).
const estimatesListRouteApi = getRouteApi('/_dashboard/dashboard/estimates');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveDisplayedUnitPrice(li: EstimateLineItem): number {
  return li.quantity > 0 ? li.lineSubtotal / li.quantity : li.basePrice;
}

function metadataString(metadata: Record<string, unknown>, key: string): string {
  const value = metadata[key];
  return typeof value === 'string' ? value : '';
}

interface EstimateDetailModel {
  wisetack?: PublicWisetackCta | null;
  title: string | null;
  status: EstimateStatus;
  estimateNumber: string;
  jobTypeName: string | null;
  poNumber: string | null;
  customer: {
    name: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
  };
  lineItems: EstimateLineItem[];
  subtotal: number;
  taxTotal: number;
  discountTotal: number;
  discountReason: string | null;
  membershipDiscountTotal: number;
  membershipPlanSnapshot: Record<string, unknown> | null;
  // NOT-584 — the two halves of the sales credit split (NOT-562), resolved
  // server-side so a rep or technician who has left the company still shows a
  // name. Null when unresolved; the row is then dropped rather than guessed at.
  //
  // REQUIRED, not optional, and deliberately so: it is the only thing tying
  // these to the mapping below. The card is unit-tested against hand-built
  // estimates, so dropping the two `raw.*` lines leaves every test green and
  // the page silently blank — marking them `?` would make that compile.
  salespersonName: string | null;
  presentedByTechnicianName: string | null;
  depositAmount: number;
  depositCollected?: boolean;
  depositRequired?: boolean;
  /** A capture covering the deposit is already clearing; see the Estimate type. */
  depositClearing?: boolean;
  depositClearingAmount?: number;
  depositClearingRail?: 'BANK' | 'CARD' | null;
  grandTotal: number;
  createdAt: string;
  updatedAt: string;
  validUntil?: string | null;
  sentAt?: string | null;
  lastViewedAt?: string | null;
  scopeOfWork: string | null;
  notes: string;
  renderedNotes: string;
  internalNotes: string;
  termsAndConditions: string;
  serviceAddress?: Estimate['serviceAddress'];
  billingAddress?: Estimate['billingAddress'];
  followUpDate: string | null;
  revisionNumber: number;
  draftVersion: number;
  previousVersions: EstimatePreviousVersion[];
  customerApprovalMethod: string | null;
  declineReason: string | null;
  approvedAt: string | null;
  rejectedAt: string | null;
  expiredAt: string | null;
  depositPaymentMethod: string | null;
  depositCollectedAt: string | null;
  /**
   * What a COLLECTED deposit actually charged. `depositAmount` is the requirement; a
   * card capture adds a service fee on top, so `chargedAmount` is the figure
   * that reconciles against the processor. Null when no deposit was collected
   * (or the caller's role has pricing masked).
   */
  depositCollectedSummary: {
    depositAmount: number;
    serviceFeeAmount: number;
    chargedAmount: number;
    discountAmount?: number;
    coveredAmount?: number;
    /** `CHEQUE_SCAN` when the deposit was a scanned check (recorded as ACH). */
    paymentFlow?: string | null;
  } | null;
  depositNotes: string | null;
  convertedJobId: string | null;
  convertedJobNumber: string | null;
  convertedJobStatus: string | null;
  originJobId: string | null;
  // NOT-560 — service call this estimate was created on / job it converted into.
  sourceJobId: string | null;
  /** The source job's number — what the work was added to, said by name. */
  sourceJobNumber: string | null;
  jobId: string | null;
  fulfillmentMode: import('@/types/estimate').EstimateFulfillmentMode | null;
  fulfilledAt: string | null;
  contactId: string | null;
  taxExemptionDrift: Estimate['taxExemptionDrift'];
}

function isNonTrivialSource(source: string): boolean {
  return Boolean(source && source.toLowerCase() !== 'manual');
}

// NOT-228: full-width in-page header (mirrors JobDetailHeader) — back breadcrumb,
// title + status, total and actions. Rendered via DetailPageLayout `header`, so
// the old WorkspaceShell context header / summary-card header are gone.
function EstimateDetailHeader({
  id,
  estimateNumber,
  title,
  subtitle,
  status,
  revisionNumber,
  total,
  actions,
  draftedByIris = false,
  relatedJobId,
  relatedJobNumber,
}: {
  id: string;
  estimateNumber: string;
  title: string;
  subtitle: string;
  status: EstimateStatus;
  revisionNumber: number;
  total: string;
  actions: ReactNode;
  draftedByIris?: boolean;
  /** The job this estimate belongs to: raised on it, authorising it, or became it. */
  relatedJobId?: string | null;
  relatedJobNumber?: string | null;
}) {
  const listSearch = estimatesListRouteApi.useSearch();
  const pricingHidden = useIsFieldHidden('PRICING');
  return (
    <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="w-full px-5 py-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Link
              to="/dashboard/estimates"
              search={listSearch}
              className="inline-flex items-center gap-0.5 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <ChevronLeft className="size-3.5" />
              Estimates · {estimateNumber}
            </Link>
            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="truncate text-xl font-semibold tracking-normal">{title}</h1>
              <StatusBadge status={status} className="rounded-md" />
              {/* Beside the status, because the status is what raises the
                  question: Converted into WHAT. It doubles as the way back for
                  anyone who reached this estimate from the job — which is how
                  they are usually reached, and the only route back was the
                  browser button or a link further down the page. */}
              {relatedJobId ? (
                <Link
                  to="/dashboard/jobs/$id"
                  params={{ id: relatedJobId }}
                  search={DEFAULT_JOB_DETAIL_SEARCH}
                  className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-0.5 text-xs font-medium text-info hover:bg-muted/60"
                  aria-label={`Open ${relatedJobNumber ?? 'the related job'}`}
                >
                  <Briefcase className="size-3" />
                  {relatedJobNumber ?? 'Job'}
                  <ArrowUpRight className="size-3" />
                </Link>
              ) : null}
              {revisionNumber > 0 ? <Badge variant="outline">Rev #{revisionNumber}</Badge> : null}
              {draftedByIris ? <IrisAttributionBadge kind="drafted" /> : null}
            </div>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {subtitle || `Estimate ${id}`}
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end">
            {!pricingHidden ? (
              <div className="text-left lg:text-right">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Total
                </p>
                <p className="text-xl font-semibold tabular-nums leading-tight">{total}</p>
              </div>
            ) : null}
            {actions}
          </div>
        </div>
      </div>
    </div>
  );
}

// NOT-228: customer + address card only — the title/status/total/actions moved to
// EstimateDetailHeader (mirrors JobCustomerCard).
//
// NOT-776: the snapshots are formatted HERE, by the estimate module's one
// `formatEstimateAddress` — the same function that writes `customer.address`
// (the billing fallback below) when the builder saves. This page used to carry
// its own copy that predated `street2`, so SERVICE and the Maps link dropped
// the suite while BILLING, formatted by the shared copy, kept it.
export function EstimateSummaryCard({
  customer,
  contactId,
  serviceAddress,
  billingAddress,
}: {
  customer: EstimateDetailModel['customer'];
  contactId: string | null;
  serviceAddress?: Estimate['serviceAddress'];
  billingAddress?: Estimate['billingAddress'];
}) {
  const service = formatEstimateAddress(serviceAddress);
  const billing = formatEstimateAddress(billingAddress) || customer.address || service;
  return (
    <section className="space-y-4 rounded-xl border border-border/70 bg-card p-5 shadow-sm">
      <SalesContactBlock
        label="Customer"
        name={customer.name}
        email={customer.email}
        phone={customer.phone}
        contactId={contactId}
        mapAddress={service}
      />
      <div className="border-t pt-4">
        <SalesAddressBlock service={service} billing={billing} />
      </div>
    </section>
  );
}

export function EstimateStatusCard({
  status,
  nextStepCopy,
  declineReason,
  convertedJobId,
  sourceJobId,
  sourceJobNumber,
  fulfillmentMode,
  fulfilledAt,
  dates,
}: {
  status: EstimateStatus;
  nextStepCopy: string;
  // Free text the customer typed on the public quote when declining.
  //
  // REQUIRED for the same reason as `salespersonName` above: this card is
  // unit-tested against hand-built props, so the test injects the very value
  // the page failed to pass. Optional here would let the call-site wiring be
  // dropped again with every test still green — required makes that a
  // compile error.
  declineReason: string | null;
  convertedJobId?: string | null;
  // NOT-560 — the service call this estimate was created on (fan-out link,
  // distinct from `convertedJobId`/`originJobId`).
  sourceJobId?: string | null;
  sourceJobNumber?: string | null;
  fulfillmentMode?: import('@/types/estimate').EstimateFulfillmentMode | null;
  fulfilledAt?: string | null;
  dates: Partial<Record<EstimateStatus, string | null | undefined>>;
}) {
  const trimmedDeclineReason = declineReason?.trim();
  return (
    <section className="rounded-xl border bg-muted/45 p-4">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
          <MoveRight size={12} />
          Next step
        </p>
        <p className="mt-1 text-[15px] font-medium text-foreground">
          {nextStepCopy}
          {(status === 'CONVERTED' || status === 'APPROVED') && convertedJobId ? (
            <>
              {' '}
              <Link
                to="/dashboard/jobs/$id"
                params={{ id: convertedJobId }}
                search={DEFAULT_JOB_DETAIL_SEARCH}
                className="text-primary hover:underline"
              >
                Open Job →
              </Link>
            </>
          ) : null}
        </p>
        {/* The customer types this on the public quote when they decline. It was
            stored but never rendered anywhere in the dashboard, so the rep had no
            way to see *why* the estimate was lost. */}
        {status === 'DECLINED' && trimmedDeclineReason ? (
          <p className="mt-2 border-l-2 border-destructive/40 pl-2.5 text-[13px] whitespace-pre-wrap text-muted-foreground">
            <span className="font-medium text-foreground">Customer&rsquo;s reason: </span>
            {trimmedDeclineReason}
          </p>
        ) : null}
        {/* NOT-560 — back-link to the service call this estimate was created on
            (fan-out), kept distinct from the 1:1 approval job link above. */}
        {sourceJobId ? (
          <Link
            to="/dashboard/jobs/$id"
            params={{ id: sourceJobId }}
            search={DEFAULT_JOB_DETAIL_SEARCH}
            className="mt-1 inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
          >
            Created from Job →
          </Link>
        ) : null}
        {sourceJobId ? (
          <p className="mt-1 text-xs text-muted-foreground">
            {getJobLinkedEstimateFulfillmentCopy(status, fulfillmentMode, fulfilledAt)}
          </p>
        ) : null}
        <SalesLifecycleStepper
          steps={buildEstimateLifecycleSteps(
            status,
            dates,
            Boolean(fulfilledAt && fulfillmentMode === 'CURRENT_JOB'),
            sourceJobNumber ?? null,
            sourceJobId ?? null,
          )}
          detours={buildEstimateLifecycleDetours(status, dates)}
        />
      </div>
    </section>
  );
}

function getJobLinkedEstimateFulfillmentCopy(
  status: EstimateStatus,
  fulfillmentMode?: import('@/types/estimate').EstimateFulfillmentMode | null,
  fulfilledAt?: string | null,
): string {
  if (fulfilledAt) {
    return fulfillmentMode === 'CURRENT_JOB'
      ? 'Work added to the source Job'
      : 'Follow-up Job created';
  }
  // An estimate that will not be approved has nothing to fulfill, whatever
  // mode it was created with.
  if (status === 'DECLINED' || status === 'EXPIRED' || status === 'REVISED') {
    return 'Not approved — nothing to fulfill';
  }
  if (fulfillmentMode === 'FOLLOW_UP_JOB') return 'Follow-up Job pending';
  // A Job-linked estimate carries its mode from creation, so this line shows
  // long before anyone approves it. Only approved work waits on a decision.
  if (status === 'APPROVED' || status === 'CONVERTED') {
    return 'Approved work awaiting a fulfillment decision';
  }
  return 'Fulfillment decided after approval';
}

// Search the job detail route requires (layout filters + $id tab); mirror of
// the job→estimate link's DEFAULT_ESTIMATE_DETAIL_SEARCH. Without it the
// TanStack v1.168 <Link> renders an href but won't navigate on click. (NOT-246)
const DEFAULT_JOB_DETAIL_SEARCH = {
  ...DEFAULT_JOB_LIST_FILTERS,
  tab: 'visits' as const,
};

const ESTIMATE_HAPPY_PATH: EstimateStatus[] = ['DRAFT', 'SENT', 'VIEWED', 'APPROVED', 'CONVERTED'];
const ESTIMATE_DETOURS: EstimateStatus[] = [
  'REVIEW',
  'FOLLOW_UP',
  'DECLINED',
  'EXPIRED',
  'REVISED',
];

const ESTIMATE_STEP_LABELS: Record<EstimateStatus, string> = {
  DRAFT: 'Draft',
  REVIEW: 'In review',
  SENT: 'Sent',
  VIEWED: 'Viewed',
  FOLLOW_UP: 'Follow-up',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  REVISED: 'Revised',
  CONVERTED: 'Converted',
};

function getEstimateDetourVariant(status: EstimateStatus): SalesLifecycleDetour['variant'] {
  if (status === 'DECLINED') return 'destructive';
  if (status === 'EXPIRED') return 'warning';
  return 'muted';
}

function buildEstimateLifecycleSteps(
  status: EstimateStatus,
  dates: Partial<Record<EstimateStatus, string | null | undefined>>,
  /**
   * The work was delivered onto the job this estimate came from. CONVERTED
   * means "became its own job", which deliberately never happens on that path —
   * so the last step sat pending forever, reading as unfinished business on an
   * estimate that is finished. It is a completed step with a different name.
   */
  fulfilledOnExistingJob = false,
  sourceJobNumber: string | null = null,
  sourceJobId: string | null = null,
): SalesLifecycleStep[] {
  const currentIndex = ESTIMATE_HAPPY_PATH.indexOf(status);
  const isDetour = ESTIMATE_DETOURS.includes(status);

  return ESTIMATE_HAPPY_PATH.map((step, index) => {
    const isCurrent = step === status;
    const isPast = currentIndex > index || (isDetour && Boolean(dates[step]));
    const date = dates[step];

    // APPROVED/CONVERTED are positive terminal states — when the estimate is
    // sitting on one of them (the "current" step), colour it green (success)
    // rather than the neutral black in-progress treatment.
    const isPositiveTerminal = step === 'APPROVED' || step === 'CONVERTED';

    if (step === 'CONVERTED' && fulfilledOnExistingJob) {
      const label = sourceJobNumber ? `Added to ${sourceJobNumber}` : 'Added to the job';
      return {
        key: step,
        label,
        dateLabel: date ? formatDate(date) : null,
        state: 'done' as const,
        variant: 'success' as const,
        // Naming the job and then leaving the reader to go find it is the same
        // dead end as not naming it.
        href: sourceJobId ? `/dashboard/jobs/${sourceJobId}` : null,
        hrefLabel: `Open ${sourceJobNumber ?? 'the job'}`,
      };
    }

    // An estimate approved in the office was never sent, so the customer-
    // facing steps did not happen on the way to Approved; ticking Sent and
    // Viewed off by position claimed a send for a customer who was told
    // nothing. The one reliable signal is `sentAt`: absent, the send AND the
    // view were skipped. Only those two: Draft and In review are office steps
    // and carry no date of their own once the status has moved on, so they
    // stay done behind whatever came after. A sent estimate keeps every past
    // step done even without a date — a view recorded before `lastViewedAt`
    // existed is still a view.
    const neverSent = !dates.SENT;
    const customerFacing = step === 'SENT' || step === 'VIEWED';
    const state = isCurrent
      ? 'current'
      : isPast
        ? date || !neverSent || !customerFacing
          ? 'done'
          : 'skipped'
        : 'pending';
    return {
      key: step,
      label: ESTIMATE_STEP_LABELS[step],
      dateLabel: date ? formatDate(date) : null,
      state,
      variant: isPositiveTerminal ? ('success' as const) : undefined,
    };
  });
}

function buildEstimateLifecycleDetours(
  status: EstimateStatus,
  dates: Partial<Record<EstimateStatus, string | null | undefined>>,
): SalesLifecycleDetour[] {
  if (!ESTIMATE_DETOURS.includes(status)) return [];
  const date = dates[status];

  return [
    {
      key: status,
      label: ESTIMATE_STEP_LABELS[status],
      dateLabel: date ? formatDate(date) : null,
      variant: getEstimateDetourVariant(status),
    },
  ];
}

function EstimateDocument({
  estimate,
  lineItems,
  publicAttachments,
  pricingMode,
  estimateId,
}: {
  estimate: EstimateDetailModel;
  lineItems: ReturnType<typeof buildLineItemRows>;
  publicAttachments: Attachment[];
  pricingMode: 'STANDARD' | 'TM_OVERRIDE';
  estimateId: string;
}) {
  const planName = (estimate.membershipPlanSnapshot?.planName as string | undefined) ?? null;
  const membershipPlans = Array.isArray(estimate.membershipPlanSnapshot?.memberships)
    ? estimate.membershipPlanSnapshot.memberships.map((membership) => {
        const snapshot = membership as Record<string, unknown>;
        return {
          planName: typeof snapshot.planName === 'string' ? snapshot.planName : null,
          savings: typeof snapshot.savings === 'number' ? snapshot.savings : null,
        };
      })
    : [];
  // NOT-584 — the plan the customer actually HOLDS, which the document's own
  // snapshot cannot tell us: pricing only records a membership that discounted
  // a line, so a member who was wrongly charged full price leaves no trace
  // there. That is the case worth surfacing (NOT-571), hence a separate read.
  const activeMembershipQuery = useContactActiveMembership(estimate.contactId ?? '');
  // `planSnapshot` is `Record<string, unknown>`, so a cast is unavoidable. These
  // two keys are the ones the pricing engine itself reads off it —
  // `membership-discount.service.ts` takes `snapshot['name']` and
  // `snapshot['discountPercent']` — so a rename there is what would silently
  // empty this badge, and that file is where to look if it ever goes blank.
  const activeMembershipPlan = activeMembershipQuery.data?.data?.planSnapshot as
    | { name?: string; discountPercent?: number }
    | undefined;
  const activeMembership = activeMembershipPlan
    ? {
        planName: activeMembershipPlan.name ?? null,
        discountPercent:
          typeof activeMembershipPlan.discountPercent === 'number'
            ? activeMembershipPlan.discountPercent
            : null,
      }
    : null;
  const pricingHidden = useIsFieldHidden('PRICING');
  // v60 named-jurisdiction tax rows. Only the sold lines feed the financial
  // summary, mirroring how the totals are computed — a declined service takes
  // its parts with it.
  const soldLines = soldLineSet(estimate.lineItems);
  const includedLines = estimate.lineItems.filter((li) => soldLines.has(li));
  const taxBreakdown = reconciledTaxBreakdown(includedLines, estimate.taxTotal).map((row) => ({
    primary: row.primary,
    secondary: row.secondary,
    rate: row.rate,
    amount: row.amount,
    jurisdictionType: row.jurisdictionType,
  }));

  return (
    <section className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="space-y-6 px-5 py-5">
        <DocumentSection title="Line items">
          <SalesLineItemsReadOnlyTable items={lineItems} emptyMessage="No line items" />
        </DocumentSection>

        <div className="ml-auto w-full sm:w-[22rem]">
          <FinancialSummaryBlock
            subtotal={estimate.subtotal}
            discountAmount={estimate.discountTotal}
            membershipDiscountPreview={estimate.membershipDiscountTotal}
            membershipPlanName={planName}
            membershipPlans={membershipPlans}
            activeMembership={activeMembership}
            taxTotal={estimate.taxTotal}
            taxBreakdown={taxBreakdown}
            grandTotal={estimate.grandTotal}
            depositAmount={estimate.depositAmount}
            depositCollected={estimate.depositCollected}
            depositCollectedSummary={estimate.depositCollectedSummary}
            depositCollectedAt={estimate.depositCollectedAt}
            depositClearing={
              // A masked role is told the deposit is clearing without the
              // amount; clearing covers the whole deposit, so it is the figure.
              estimate.depositClearing &&
              ((estimate.depositClearingAmount ?? 0) > 0 || (estimate.depositAmount ?? 0) > 0)
                ? {
                    amount: estimate.depositClearingAmount || (estimate.depositAmount ?? 0),
                    rail: estimate.depositClearingRail ?? null,
                  }
                : null
            }
            depositServiceFee={estimate.depositCollectedSummary?.serviceFeeAmount ?? 0}
            pricingMode={pricingMode}
          />
          {estimate.discountReason?.trim() ? (
            <p className="mt-2 rounded-lg border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Discount reason:</span>{' '}
              {estimate.discountReason.trim()}
            </p>
          ) : null}
        </div>

        {/* Payment schedule sits with the document (right below line items /
            totals) — it's part of what the customer agrees to, not buried in
            the internal "Also here" section. Gated on PRICING visibility to
            match the financial summary above it. */}
        {!pricingHidden ? (
          <PaymentScheduleSection
            depositAmount={estimate.depositAmount}
            depositRequired={estimate.depositRequired}
            estimateId={estimateId}
            grandTotal={estimate.grandTotal}
            estimateStatus={estimate.status}
          />
        ) : null}

        <DocumentTextSection title="Scope of work" value={estimate.scopeOfWork} allowHtml />
        <DocumentTextSection title="Notes to customer" value={estimate.renderedNotes} allowHtml />
        {/* Resolve terms exactly as the PDF and public quote do, so this
            preview shows what the customer will read: unfilled fill-in-the-blank
            clauses stripped, and `hasTerms` deciding whether the section exists
            at all — `<p></p>` from the editor is not terms, and rendering it
            left a "Terms" heading over blank space. */}
        {(() => {
          const terms = resolveCustomerTerms(estimate.termsAndConditions);
          return terms.hasTerms ? (
            <DocumentTextSection title="Terms" value={terms.text} allowHtml />
          ) : null;
        })()}

        {publicAttachments.length > 0 ? (
          <DocumentSection title={`Customer attachments (${publicAttachments.length})`}>
            <EstimateAttachments
              estimateId={estimateId}
              attachments={publicAttachments}
              onAttachmentsChange={() => {}}
              readOnly
            />
          </DocumentSection>
        ) : null}

        <CustomFieldsSection
          entityType="estimate"
          entityId={estimateId}
          customerFacingFilter={true}
        />
      </div>
    </section>
  );
}

function DocumentSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        <Separator className="flex-1" />
      </div>
      {children}
    </div>
  );
}

function DocumentTextSection({
  title,
  value,
  allowHtml = false,
}: {
  title: string;
  value?: string | null;
  allowHtml?: boolean;
}) {
  if (!value?.trim()) return null;
  return (
    <DocumentSection title={title}>
      {allowHtml && isHtmlContent(value) ? (
        <div
          className={`prose prose-sm min-w-0 max-w-none break-words text-sm text-foreground [overflow-wrap:anywhere] [&_*]:break-words [&_*]:[overflow-wrap:anywhere] ${RICH_TEXT_CLASS}`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
        />
      ) : (
        <p className="min-w-0 whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground [overflow-wrap:anywhere]">
          {value}
        </p>
      )}
    </DocumentSection>
  );
}

/** Exported for test. Rendering it needs no route context. */
export function EstimateOperationalDetailsCard({
  estimate,
  source,
  businessUnitName,
}: {
  estimate: EstimateDetailModel;
  source: string;
  /**
   * NOT-542 — the estimate's Business Unit, already resolved to its name by the
   * caller. Passed in rather than looked up here so the card stays renderable
   * without a query client, and so a tenant with no units simply passes null and
   * the row drops out with the rest of the empty ones.
   */
  businessUnitName?: string | null;
}) {
  // Before the approval this is the method chosen at send time (it gates the
  // public page); after it, how the customer actually approved — the office
  // path records VERBAL rather than leaving a SIGNATURE intent standing.
  const approvalMethod = estimate.customerApprovalMethod
    ? formatLabel(estimate.customerApprovalMethod)
    : null;
  const rows = [
    { label: 'Updated', value: estimate.updatedAt ? formatDate(estimate.updatedAt) : null },
    { label: 'Sent', value: estimate.sentAt ? formatDate(estimate.sentAt) : null },
    { label: 'Source', value: source ? formatEstimateSource(source) : null },
    // NOT-584 — "Job type" everywhere, including the PDF and the public quote
    // page; "Service type" is gone from the estimate surfaces entirely.
    { label: 'Job type', value: estimate.jobTypeName?.trim() || null },
    // NOT-542 — the unit the estimate was assigned to. It was chosen on the form
    // and stored, but until now no read surface showed it back.
    { label: 'Business unit', value: businessUnitName?.trim() || null },
    { label: 'PO number', value: estimate.poNumber?.trim() || null },
    // NOT-584 — both halves of the credit split (NOT-562), read-only because
    // `presentedByTechnicianId` is immutable presentation attribution.
    //
    // Gated on the resolved NAME rather than on `attributionQuality`. The two
    // are equivalent today (quality is EXACT_CREATOR iff a techId was written),
    // but nothing yet writes the derived value the schema anticipates — if one
    // is ever added, this row would present it as exact and the label would
    // need to say otherwise. The `.trim()` is load-bearing: a whitespace name
    // would otherwise hold a row open and render blank.
    { label: 'Salesperson', value: estimate.salespersonName?.trim() || null },
    {
      label: 'Presented by',
      value: estimate.presentedByTechnicianName?.trim() || null,
    },
    { label: 'Approval method', value: approvalMethod },
    // `validUntil` is a `@db.Date` calendar day, not an instant — it must read
    // the same here as it does on the customer document beside it.
    {
      label: 'Valid until',
      value: estimate.validUntil ? formatDateOnly(estimate.validUntil) : null,
    },
  ].filter((row): row is { label: string; value: string } => Boolean(row.value));

  if (rows.length === 0) return null;

  return (
    <DetailCard label="Details" icon={Clock3}>
      <DetailFieldGrid>
        {rows.map((row) => (
          <DetailField key={row.label} label={row.label} value={row.value} />
        ))}
      </DetailFieldGrid>
    </DetailCard>
  );
}

function RecurringScheduleCard({ raw }: { raw: Record<string, unknown> }) {
  if (raw.bookingType !== 'RECURRING' || !raw.recurringConfig) return null;
  const rc = raw.recurringConfig as Record<string, unknown>;
  return (
    <DetailCard label="Recurring schedule" icon={CalendarClock}>
      <DetailFieldGrid>
        <DetailField
          label="Frequency"
          value={
            String(rc.frequency).toLowerCase() +
            (rc.frequency === 'CUSTOM' && rc.interval && rc.intervalUnit
              ? ` · every ${rc.interval} ${String(rc.intervalUnit).toLowerCase()}`
              : '')
          }
        />
        {rc.startDate ? (
          <DetailField label="Starts" value={formatRecurringDate(String(rc.startDate))} />
        ) : null}
        {rc.endType ? (
          <DetailField
            label="Ends"
            value={
              rc.endType === 'INDEFINITE'
                ? 'Never'
                : rc.endType === 'AFTER_COUNT'
                  ? `After ${rc.endAfterCount ?? 0} visits`
                  : rc.endOnDate
                    ? formatRecurringDate(String(rc.endOnDate))
                    : '—'
            }
          />
        ) : null}
      </DetailFieldGrid>
    </DetailCard>
  );
}

function DepositCollectedBanner({ estimate }: { estimate: EstimateDetailModel }) {
  if (!(estimate.depositAmount > 0 && estimate.depositCollected)) return null;
  const summary = estimate.depositCollectedSummary;
  const discount = summary?.discountAmount ?? 0;
  const serviceFee = summary?.serviceFeeAmount ?? 0;
  const charged = estimate.depositCollectedSummary?.chargedAmount ?? estimate.depositAmount;
  // A scanned check is recorded as the ACH debit it became; it reads as the
  // check it was, as on every other payment surface.
  const depositMethod = estimate.depositPaymentMethod
    ? paymentMethodLabel(estimate.depositPaymentMethod, summary?.paymentFlow, (method) => method)
    : null;
  return (
    <div className="rounded-xl border border-success/30 bg-success/10 px-4 py-3 text-success">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm font-semibold">
          <Check className="size-4" />
          {summary ? 'Deposit collected' : 'Deposit covered'}
        </span>
        <span className="text-sm font-semibold tabular-nums">{formatCurrency(charged)}</span>
      </div>
      {discount > 0 ? (
        <p className="mt-1 text-xs font-medium text-success">
          {formatCurrency(discount)} ACH/check discount ·{' '}
          {formatCurrency(summary?.coveredAmount ?? estimate.depositAmount)} deposit covered
        </p>
      ) : null}
      {serviceFee > 0 ? (
        <p className="mt-1 text-xs font-medium text-success" data-testid="deposit-charged-note">
          {formatCurrency(charged)} card price · ACH/check savings not applied:{' '}
          {formatCurrency(serviceFee)}
        </p>
      ) : null}
      {[
        depositMethod,
        estimate.depositCollectedAt && formatDate(estimate.depositCollectedAt),
      ].filter(Boolean).length > 0 ? (
        <p className="mt-1 text-xs text-success">
          {[depositMethod, estimate.depositCollectedAt && formatDate(estimate.depositCollectedAt)]
            .filter(Boolean)
            .join(' · ')}
        </p>
      ) : null}
      {estimate.depositNotes ? (
        <p className="mt-1 text-xs text-success">{estimate.depositNotes}</p>
      ) : null}
    </div>
  );
}

// In-context warning shown when the estimate's frozen tax-exemption no longer
// matches the linked customer/address. Re-pricing (with a refreshed
// address-level exemption read) resolves it; the banner then disappears once
// the detail refetches. "Keep as-is" dismisses locally without an API call.
function TaxExemptionDriftBanner({
  estimateId,
  drift,
}: {
  estimateId: string;
  drift: NonNullable<EstimateDetailModel['taxExemptionDrift']>;
}) {
  const [dismissed, setDismissed] = useState(false);
  const reprice = useRepriceEstimate();

  if (!drift.hasDrift || dismissed) return null;

  const subject =
    drift.source === 'CUSTOMER' ? 'customer' : drift.source === 'ADDRESS' ? 'address' : null;
  const message =
    drift.currentlyExempt && !drift.documentExempt
      ? subject
        ? `This estimate's ${subject} is now tax-exempt. Re-price to remove tax.`
        : 'This estimate now qualifies for a tax exemption. Re-price to remove tax.'
      : 'The tax exemption applied to this estimate no longer applies. Re-price to apply tax.';

  return (
    <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-warning">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className="flex items-start gap-2 text-sm font-medium">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>{message}</span>
        </span>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            disabled={reprice.isPending}
          >
            Keep as-is
          </Button>
          <Button
            size="sm"
            onClick={() => reprice.mutate({ id: estimateId, refreshTaxExemption: true })}
            disabled={reprice.isPending}
          >
            {reprice.isPending ? 'Re-pricing…' : 'Re-price'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function buildLineItemRows(
  lineItems: EstimateLineItem[],
  pricingMode: 'STANDARD' | 'TM_OVERRIDE',
  pricingHidden: boolean,
  acceptedScope = false,
) {
  return lineItems.map((li) => ({
    id: li.lineItemId,
    name: li.name,
    code: li.code,
    type: li.type,
    quantity: li.quantity,
    unit: String(li.type).toUpperCase() === 'MEMBERSHIP' ? null : li.unit,
    unitPrice: resolveDisplayedUnitPrice(li),
    // What decides whether this row has a price to state at all. Without them
    // the internal table cannot tell a part that charges nothing from one
    // genuinely priced at $0.00, and prints the same figure for both.
    chargeable: li.chargeable,
    pricingTreatment: li.pricingTreatment,
    tax: li.lineTax,
    // NOT-590 — the keys that say which of these rows are one sale. Without
    // them a service and its parts render as flat siblings and the operator
    // cannot tell that the parent's tax covers all of them.
    lineKey: li.lineKey,
    parentLineKey: li.parentLineKey,
    componentDisplayMode: li.componentDisplayMode,
    isOptional: li.isOptional,
    isSelected: li.isSelected,
    lineSubtotal: li.lineSubtotal,
    taxLineType: li.taxLineType,
    taxBreakdown: reconciledTaxBreakdown([li], li.lineTax),
    total: li.lineTotal,
    detail: (
      <div className="mt-0.5 space-y-0.5">
        {li.description ? <p className="text-xs text-muted-foreground">{li.description}</p> : null}
        {pricesFromHours(
          {
            type: li.type,
            priceStrategy: li.priceStrategy,
            // The two tells a STORED line gives — the same pair the editor
            // reads. A hand-typed line prices flat, so it has no basis to show.
            isCustom: li.source === 'MANUAL' || !String(li.code ?? '').trim(),
          },
          pricingMode,
        ) ? (
          <DynamicBasisText line={li} pricingHidden={pricingHidden} />
        ) : null}
      </div>
    ),
    badges: lineItemBadges(li, acceptedScope),
  }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function EstimateDetailPage() {
  const router = useRouter();
  const { id } = Route.useParams();
  const childMatches = useChildMatches();
  const pricingHidden = useIsFieldHidden('PRICING');
  const canViewCompany = useHasPermission('company:view');

  const { data: rawEstimate, isLoading, isError, error } = useEstimate(id);
  const { data: wisetackSettings } = useWisetackSettings({ enabled: canViewCompany });
  // NOT-542 — the estimate stores a `businessUnitId`; resolve it to the unit's
  // name here (archived units still resolve) so the details card can show it.
  const { labelFor: businessUnitLabelFor } = useBusinessUnitLabel();

  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(t);
  }, [isLoading]);

  const { data: attachmentData = [] } = useEstimateAttachments(id);
  const rawU = rawEstimate as unknown as Record<string, unknown> | undefined;
  const raw: Record<string, unknown> =
    (rawU?.estimate as Record<string, unknown> | undefined) ?? rawU ?? {};
  const metadata = (raw.metadata as Record<string, unknown> | null | undefined) ?? {};

  // When navigating to a child route (e.g. /edit), render it instead of detail
  if (childMatches.length > 0) {
    return <Outlet />;
  }

  // -- Loading ---------------------------------------------------------------

  if (isLoading) {
    if (!showSkeleton) return null;
    return (
      <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // -- Error -----------------------------------------------------------------

  if (isError || !rawEstimate) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Estimate not found'}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            router.navigate({
              to: '/dashboard/estimates',
              search: { status: 'all', q: '', page: 1 } as never,
            })
          }
        >
          <ArrowLeft className="mr-1 size-4" />
          Back to Estimates
        </Button>
      </div>
    );
  }

  // -- Normalize estimate fields -----------------------------------------------

  const rawCustomer = (raw.customer ?? { name: '', email: '', phone: '', address: '' }) as {
    name?: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
  };
  const estimate: EstimateDetailModel = {
    wisetack: (raw.wisetack as PublicWisetackCta | null | undefined) ?? null,
    title: (raw.title as string | null | undefined) ?? null,
    status: raw.status as EstimateStatus,
    estimateNumber: String(raw.estimateNumber ?? ''),
    jobTypeName: (raw.jobTypeName as string | null | undefined) ?? null,
    poNumber: (raw.poNumber as string | null | undefined) ?? null,
    customer: {
      name: rawCustomer.name || 'Unknown customer',
      email: rawCustomer.email ?? null,
      phone: rawCustomer.phone ?? null,
      address: rawCustomer.address ?? null,
    },
    lineItems: ((raw.lineItems as Record<string, unknown>[]) ?? []).map((li) => {
      const amounts = resolveEstimateLineAmounts(li);
      return {
        ...li,
        basePrice: Number(li.basePrice) || 0,
        laborRate: Number(li.laborRate) || 0,
        quantity: Number(li.quantity) || 0,
        lineTotal: amounts.lineTotal,
        lineTax: amounts.lineTax,
        lineSubtotal: amounts.lineSubtotal,
        estimatedHours: li.estimatedHours != null ? Number(li.estimatedHours) : undefined,
        priceStrategy: String(li.priceStrategy ?? 'FLAT'),
      } as unknown as EstimateLineItem;
    }),
    subtotal: Number(raw.subtotal) || 0,
    taxTotal: Number(raw.taxTotal) || 0,
    discountTotal: Number(raw.discountTotal) || 0,
    discountReason: (raw.discountReason as string | null | undefined) ?? null,
    membershipDiscountTotal: Number(raw.membershipDiscountTotal) || 0,
    membershipPlanSnapshot: (raw.membershipPlanSnapshot as Record<string, unknown> | null) ?? null,
    salespersonName: (raw.salespersonName as string | null | undefined) ?? null,
    presentedByTechnicianName: (raw.presentedByTechnicianName as string | null | undefined) ?? null,
    depositAmount: Number(raw.depositAmount) || 0,
    depositCollected: raw.depositCollected as boolean | undefined,
    depositRequired: raw.depositRequired as boolean | undefined,
    // Inside `estimate`, not a response sibling: `useEstimate` spreads the row
    // whole, so these ride along with no extra plumbing — unlike
    // `depositCollectedSummary` below, which has to be read from both shapes.
    depositClearing: raw.depositClearing as boolean | undefined,
    depositClearingAmount: raw.depositClearingAmount as number | undefined,
    depositClearingRail: (raw.depositClearingRail as 'BANK' | 'CARD' | null | undefined) ?? null,
    grandTotal: Number(raw.grandTotal) || 0,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: String(raw.updatedAt ?? ''),
    validUntil: raw.validUntil as string | null | undefined,
    sentAt: raw.sentAt as string | null | undefined,
    lastViewedAt: (raw.lastViewedAt as string | null | undefined) ?? null,
    scopeOfWork: (raw.scopeOfWork as string | null | undefined) ?? null,
    notes: String(raw.notes ?? ''),
    renderedNotes: String(raw.renderedNotes ?? raw.notes ?? ''),
    internalNotes: String(raw.internalNotes ?? ''),
    termsAndConditions: String(raw.termsAndConditions ?? ''),
    serviceAddress: raw.serviceAddress as Estimate['serviceAddress'],
    billingAddress: raw.billingAddress as Estimate['billingAddress'],
    followUpDate: (raw.followUpDate as string | null | undefined) ?? null,
    revisionNumber: Number(raw.revisionNumber) || 0,
    draftVersion: Number(raw.draftVersion) || 0,
    previousVersions: (raw.previousVersions as EstimatePreviousVersion[]) ?? [],
    customerApprovalMethod: (raw.customerApprovalMethod as string | null | undefined) ?? null,
    declineReason: (raw.declineReason as string | null | undefined) ?? null,
    approvedAt: (raw.approvedAt as string | null | undefined) ?? null,
    rejectedAt: (raw.rejectedAt as string | null | undefined) ?? null,
    expiredAt: (raw.expiredAt as string | null | undefined) ?? null,
    depositPaymentMethod: (raw.depositPaymentMethod as string | null | undefined) ?? null,
    depositCollectedAt: (raw.depositCollectedAt as string | null | undefined) ?? null,
    // Sits beside `estimate` on the response, not inside it — read both shapes
    // so this survives however the payload is unwrapped.
    depositCollectedSummary:
      ((rawU?.depositCollectedSummary ?? raw.depositCollectedSummary) as
        | EstimateDetailModel['depositCollectedSummary']
        | undefined) ?? null,
    depositNotes: (raw.depositNotes as string | null | undefined) ?? null,
    convertedJobId:
      (raw.convertedJob as { id?: string } | null | undefined)?.id ||
      (raw.convertedJobId as string | null | undefined) ||
      metadataString(metadata, 'convertedJobId') ||
      metadataString(metadata, 'jobId') ||
      null,
    convertedJobNumber:
      (raw.convertedJob as { jobNumber?: string } | null | undefined)?.jobNumber ?? null,
    convertedJobStatus:
      (raw.convertedJob as { statusPhase?: string } | null | undefined)?.statusPhase ?? null,
    originJobId: (raw.originJobId as string | null | undefined) ?? null,
    // NOT-560 — the service call this estimate was created on, and the job it
    // converted into (distinct from originJobId's 1:1 approval link).
    sourceJobId: (raw.sourceJobId as string | null | undefined) ?? null,
    sourceJobNumber:
      (raw.jobReference as { jobNumber?: string } | null | undefined)?.jobNumber ?? null,
    jobId: (raw.jobId as string | null | undefined) ?? null,
    fulfillmentMode:
      raw.fulfillmentMode === 'CURRENT_JOB' ||
      raw.fulfillmentMode === 'FOLLOW_UP_JOB' ||
      raw.fulfillmentMode === 'UNDECIDED'
        ? raw.fulfillmentMode
        : null,
    fulfilledAt: (raw.fulfilledAt as string | null | undefined) ?? null,
    contactId: (raw.contactId as string | null | undefined) ?? null,
    taxExemptionDrift: (raw.taxExemptionDrift as Estimate['taxExemptionDrift'] | undefined) ?? null,
  };
  const pricingMode = raw.pricingMode === 'TM_OVERRIDE' ? 'TM_OVERRIDE' : 'STANDARD';
  const estimateSource = metadataString(metadata, 'source');
  const customerName = estimate.customer.name || 'Unknown customer';
  const hasCustomTitle = Boolean(estimate.title?.trim());
  const estimateTitle = estimate.title?.trim() || `${customerName} - Estimate`;
  const estimateSourceLabel = isNonTrivialSource(estimateSource)
    ? formatEstimateSource(estimateSource)
    : '';
  const estimateSubtitle = estimateHeaderSubtitle({
    estimateNumber: estimate.estimateNumber,
    customerName: hasCustomTitle ? customerName : '',
    createdAt: estimate.createdAt,
    validUntil: estimate.validUntil,
    sourceLabel: estimateSourceLabel,
    formatInstant: formatDate,
  });
  const defaultNextStepCopy = getEstimateNextStepCopy({
    status: estimate.status,
    followUpDate: estimate.followUpDate,
    lastViewedAt: estimate.lastViewedAt,
    convertedJobId: estimate.convertedJobId,
    convertedJobNumber: estimate.convertedJobNumber,
    linkedJobInactive: ['CANCELLED', 'CLOSED', 'ARCHIVED'].includes(
      estimate.convertedJobStatus ?? '',
    ),
    // Already delivered onto the job it came from — there is no conversion
    // left, and the copy has to stop asking for one.
    fulfilledOnExistingJob: Boolean(
      estimate.fulfilledAt && estimate.fulfillmentMode === 'CURRENT_JOB',
    ),
    sourceJobNumber: estimate.sourceJobNumber,
    formatDate,
  });
  const nextStepCopy =
    estimate.status === 'APPROVED' && estimate.sourceJobId && !estimate.fulfilledAt
      ? estimate.fulfillmentMode === 'FOLLOW_UP_JOB'
        ? 'Create the follow-up Job when any required deposit is ready.'
        : 'Choose whether to add this work to the source Job, create a follow-up Job, ' +
          'or send it to dispatch.'
      : defaultNextStepCopy;
  const status: EstimateStatus = estimate.status;
  const publicAttachments = attachmentData.filter((a) => !a.isInternal);
  const internalAttachments = attachmentData.filter((a) => a.isInternal);
  const hasInternalNotes = estimate.internalNotes.trim().length > 0;
  // Optional items render inline while this is still a proposal. Once accepted,
  // the detail becomes the sold document: declined rows disappear and selected
  // add-ons say "Accepted option" so the operator cannot confuse offered scope
  // with work the customer bought. Stored rows remain intact for audit/history.
  const acceptedEstimate = estimateForAcceptedScope(estimate);
  const acceptedScope = hasAcceptedEstimateScope(estimate.status);
  const lineItemRows = buildLineItemRows(
    acceptedEstimate.lineItems,
    pricingMode,
    pricingHidden,
    acceptedScope,
  );
  const hasAlsoHere =
    // Payment-schedule content moved into the document (below line items), so
    // it no longer keeps "Also here" alive — this section is now only the
    // deposit-collected banner + internal-only cards.
    (!pricingHidden && estimate.depositAmount > 0 && Boolean(estimate.depositCollected)) ||
    hasInternalNotes ||
    internalAttachments.length > 0 ||
    estimate.previousVersions.length > 0;
  const statusDates: Partial<Record<EstimateStatus, string | null | undefined>> = {
    DRAFT: estimate.createdAt,
    REVIEW: estimate.status === 'REVIEW' ? estimate.updatedAt : undefined,
    SENT: estimate.sentAt,
    // Prefer the dedicated last-viewed stamp (refreshed on every customer view)
    // so the Viewed step keeps an accurate date even after the estimate has
    // advanced. Fall back to updatedAt for legacy rows with no stamp yet.
    VIEWED:
      estimate.lastViewedAt ?? (estimate.status === 'VIEWED' ? estimate.updatedAt : undefined),
    FOLLOW_UP: estimate.followUpDate,
    APPROVED: estimate.approvedAt,
    DECLINED: estimate.rejectedAt,
    EXPIRED: estimate.expiredAt ?? estimate.validUntil,
    REVISED: estimate.status === 'REVISED' ? estimate.updatedAt : undefined,
    CONVERTED: estimate.status === 'CONVERTED' ? estimate.updatedAt : undefined,
  };

  // -- Render ----------------------------------------------------------------

  return (
    <DetailPageLayout
      key={id}
      header={
        <EstimateDetailHeader
          id={id}
          estimateNumber={estimate.estimateNumber}
          title={estimateTitle}
          subtitle={estimateSubtitle}
          status={status}
          revisionNumber={estimate.revisionNumber}
          total={formatCurrency(estimate.grandTotal)}
          draftedByIris={metadataString(metadata, 'createdByRole') === 'AI'}
          // Whichever job this estimate belongs to: the one it was raised on,
          // the one it authorises, or the one it became.
          relatedJobId={estimate.sourceJobId ?? estimate.originJobId ?? estimate.jobId}
          relatedJobNumber={estimate.sourceJobNumber ?? estimate.convertedJobNumber}
          actions={
            <EstimateActions
              id={id}
              estimateNumber={estimate.estimateNumber}
              status={status}
              sentAt={estimate.sentAt}
              variant="header-row"
              depositAmount={estimate.depositAmount}
              depositCollected={estimate.depositCollected}
              depositRequired={estimate.depositRequired}
              depositClearing={estimate.depositClearing}
              depositClearingAmount={estimate.depositClearingAmount}
              depositClearingRail={estimate.depositClearingRail}
              linkedJobId={estimate.originJobId ?? estimate.sourceJobId}
              sourceJobId={estimate.sourceJobId}
              fulfillmentMode={estimate.fulfillmentMode}
              fulfilledAt={estimate.fulfilledAt}
              convertedJobId={estimate.jobId}
              linkedJobInactive={['CANCELLED', 'CLOSED', 'ARCHIVED'].includes(
                estimate.convertedJobStatus ?? '',
              )}
            />
          }
        />
      }
      headerClassName="block border-0 p-0"
    >
      <EstimateSummaryCard
        customer={estimate.customer}
        contactId={estimate.contactId}
        serviceAddress={estimate.serviceAddress}
        billingAddress={estimate.billingAddress}
      />

      {estimate.taxExemptionDrift?.hasDrift ? (
        <TaxExemptionDriftBanner estimateId={id} drift={estimate.taxExemptionDrift} />
      ) : null}

      <EstimateStatusCard
        status={status}
        nextStepCopy={nextStepCopy}
        declineReason={estimate.declineReason}
        convertedJobId={estimate.convertedJobId}
        sourceJobId={estimate.sourceJobId}
        sourceJobNumber={estimate.sourceJobNumber}
        fulfillmentMode={estimate.fulfillmentMode}
        fulfilledAt={estimate.fulfilledAt}
        dates={statusDates}
      />

      <WisetackContextCard
        context="estimate"
        settings={wisetackSettings}
        wisetack={estimate.wisetack}
        contactId={estimate.contactId}
        customerEmail={estimate.customer.email}
        customerPhone={estimate.customer.phone}
      />

      <EstimateDocument
        estimate={estimate}
        lineItems={lineItemRows}
        publicAttachments={publicAttachments as Attachment[]}
        pricingMode={pricingMode}
        estimateId={id}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <EstimateOperationalDetailsCard
          estimate={estimate}
          source={estimateSource}
          businessUnitName={businessUnitLabelFor(raw.businessUnitId as string | null | undefined)}
        />
        <RecurringScheduleCard raw={raw} />
      </div>

      <DetailCard label="Activity" icon={FileText} bodyClassName="p-0">
        <AuditTrail entityType="estimate" entityId={id} alwaysOpen />
      </DetailCard>

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <TagsIcon className="size-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">Internal</h2>
          </div>
          <Separator className="flex-1" />
        </div>
        <p className="text-xs text-muted-foreground">
          Tags and custom fields are internal organisational metadata — they are not shown on the
          customer document and can be edited in any status.
        </p>
        <div className="grid gap-4">
          <EstimateTagsCard estimateId={id} tags={(raw.tags as { tagId: string }[]) ?? []} />
          <EstimateInternalCustomFieldsCard estimateId={id} />
        </div>
      </section>

      {hasAlsoHere ? (
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Lock className="size-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Also here</h2>
            </div>
            <Separator className="flex-1" />
          </div>

          <div className="grid gap-4">
            {/* Payment schedule moved up into the document (below line items).
                The deposit-collected banner stays here as internal context. */}
            {!pricingHidden ? <DepositCollectedBanner estimate={estimate} /> : null}

            {hasInternalNotes ? (
              <DetailCard label="Internal notes">
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {estimate.internalNotes}
                </p>
              </DetailCard>
            ) : null}

            {internalAttachments.length > 0 ? (
              <DetailCard label={`Internal files (${internalAttachments.length})`} icon={Paperclip}>
                <EstimateAttachments
                  estimateId={id}
                  attachments={internalAttachments as Attachment[]}
                  onAttachmentsChange={() => {}}
                  readOnly
                />
              </DetailCard>
            ) : null}

            {estimate.previousVersions.length > 0 ? (
              <DetailCard
                label={`Version history (${estimate.previousVersions.length})`}
                bodyClassName="space-y-3"
              >
                {[...estimate.previousVersions]
                  .reverse()
                  .map((v: EstimatePreviousVersion, i: number) => (
                    <div
                      key={`${v.revisionNumber}-${i}`}
                      className="space-y-2 rounded-lg border p-3"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Rev {v.revisionNumber}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(v.snapshotAt)}
                        </span>
                      </div>
                      <DetailFieldGrid>
                        <DetailField label="Status at snapshot" value={formatLabel(v.status)} />
                        {!pricingHidden ? (
                          <>
                            <DetailField
                              label="Total"
                              value={formatCurrency(Number(v.grandTotal))}
                            />
                            <DetailField
                              label="Subtotal"
                              value={formatCurrency(Number(v.subtotal))}
                            />
                            {Number(v.discountTotal) > 0 ? (
                              <DetailField
                                label="Discount"
                                value={`-${formatCurrency(Number(v.discountTotal))}`}
                              />
                            ) : null}
                          </>
                        ) : null}
                        <DetailField label="Items" value={v.lineItems.length} />
                      </DetailFieldGrid>
                    </div>
                  ))}
              </DetailCard>
            ) : null}
          </div>
        </section>
      ) : null}
    </DetailPageLayout>
  );
}

// ---------------------------------------------------------------------------
// Custom Fields Section (read-only)
// ---------------------------------------------------------------------------

function CustomFieldsSection({
  entityType,
  entityId,
  customerFacingFilter,
}: {
  entityType: string;
  entityId: string;
  customerFacingFilter?: boolean;
}) {
  const { data: valuesData } = useCustomFieldValues(entityType as CustomFieldEntityType, entityId);
  const { data: schemaData } = useCustomFieldSchema(entityType as CustomFieldEntityType);
  const allFields = Array.isArray(valuesData) ? valuesData : [];
  const fields =
    customerFacingFilter === undefined
      ? allFields
      : allFields.filter((f) => f.isCustomerFacing === customerFacingFilter);
  const schemaMap = new Map(
    (Array.isArray(schemaData) ? schemaData : []).map((s) => [s.definition.id, s]),
  );
  if (fields.length === 0) return null;

  return (
    <DetailCard label="Custom Fields">
      <DetailFieldGrid>
        {fields.map((field) => {
          const schema = schemaMap.get(field.fieldDefinitionId);
          const display = schema
            ? formatCustomFieldDisplayValue(schema, field.value)
            : String(field.value ?? '—');
          return <DetailField key={field.fieldDefinitionId} label={field.label} value={display} />;
        })}
      </DetailFieldGrid>
    </DetailCard>
  );
}

// ---------------------------------------------------------------------------
// Internal section — Tags + editable internal Custom Fields (NOT-247)
// Editable in any status; neither appears on the customer document.
// ---------------------------------------------------------------------------

function EstimateTagsCard({ estimateId, tags }: { estimateId: string; tags: { tagId: string }[] }) {
  const serverTagIds = useMemo(() => tags.map((t) => t.tagId), [tags]);
  const [value, setValue] = useState<string[]>(serverTagIds);
  // Re-sync local selection when the server data changes (e.g. after refetch).
  useEffect(() => {
    setValue(serverTagIds);
  }, [serverTagIds]);

  const replaceTags = useReplaceEstimateTags(estimateId);

  function handleChange(next: string[]) {
    setValue(next);
    replaceTags.mutate({ tagIds: next });
  }

  return (
    <DetailCard label="Tags">
      <TagSelector scope="estimate" value={value} onChange={handleChange} variant="input" />
    </DetailCard>
  );
}

function EstimateInternalCustomFieldsCard({ estimateId }: { estimateId: string }) {
  const saveValues = useSaveEstimateCustomFieldValues(estimateId);

  return (
    <CustomFieldsReadEditCard
      entityType="estimate"
      entityId={estimateId}
      title="Custom fields"
      customerFacingFilter={false}
      saveValues={(values) => saveValues.mutateAsync(values)}
    />
  );
}
